/**
 * Parse user agent string to get platform-specific properties
 *
 * @param {string} userAgent - The user agent string
 * @returns {Object} Extended properties with platform-specific data
 */
const parseUserAgent = (userAgent) => {
  // Check for native app identifier with optional API version for Android
  const nativeAppMatch = userAgent.match(/native_app_(ios|android(?:_(\d+))?)/i);
  if (!nativeAppMatch) {
    // Explicitly set WebView to false so any persisted Mixpanel super prop value is cleared.
    return { WebView: false };
  }

  const platform = nativeAppMatch[1].toLowerCase().split('_')[0]; // Extract 'ios' or 'android'
  const androidApiVersion = nativeAppMatch[2]; // Will be undefined for iOS

  const baseProps = {
    product: platform,
    platform: platform === 'android' ? 'android' : undefined,
    // native_app_* in the UA is the authoritative signal for a native in-app WebView.
    WebView: true,
    $browser: userAgent,
  };

  // Add Android API version if available from our custom identifier
  if (platform === 'android' && androidApiVersion) {
    return {
      ...baseProps,
      'Android API Version': parseInt(androidApiVersion, 10),
    };
  }

  return baseProps;
};

/**
 * Return user agent properties for mixpanel merged with the provided super properties.
 * Always includes WebView: true/false based on the native_app_* identifier in the UA.
 *
 * @param {Object} superProps - The super properties
 * @returns {Object} The merged user agent and super properties
 */
export default function getMixpanelUserAgentProps(superProps) {
  const userAgentProps = parseUserAgent(window.navigator.userAgent);
  const mergedProps = {
    ...userAgentProps,
    ...superProps,
  };

  return mergedProps;
}
